import { createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/Card';
import { useRecipesList } from '@/features/recipes/api';
import { HexBadge } from '@/components/HexBadge';

export const Route = createFileRoute('/_authed/')({
  component: Dashboard,
});

function Dashboard() {
  const own = useRecipesList({ mine: true, public: false });
  const pub = useRecipesList({ mine: false, public: true });

  const ownRecipes = own.data ?? [];
  const publicRecipes = pub.data ?? [];

  const editorial = publicRecipes.filter((r) => r.source === 'editorial').length;
  const community = publicRecipes.filter((r) => r.source === 'community').length;
  const publicTotal = publicRecipes.length;
  const ownDrafts = ownRecipes.filter((r) => r.visibility === 'private').length;

  const loading = own.isLoading || pub.isLoading;

  return (
    <div data-testid="admin-dashboard" className="p-4 sm:p-6 lg:p-8">
      <header className="mb-8 flex items-center gap-4">
        <HexBadge size={36} />
        <div>
          <h1 className="font-serif text-3xl leading-tight">Dashboard</h1>
          <p className="text-ink-soft text-sm">Content health across Honeycomb.</p>
        </div>
      </header>

      {loading ? (
        <div className="text-ink-soft">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <StatTile label="Public recipes" value={publicTotal} tone="honey" />
          <StatTile label="Editorial" value={editorial} tone="honey" />
          <StatTile label="Community" value={community} tone="sage" />
          <StatTile label="My drafts" value={ownDrafts} tone="neutral" />
        </div>
      )}
    </div>
  );
}

type Tone = 'honey' | 'sage' | 'neutral';

function StatTile({ label, value, tone }: { label: string; value: number; tone: Tone }) {
  const accent: Record<Tone, string> = {
    honey: 'var(--color-honey)',
    sage: 'var(--color-sage)',
    neutral: 'var(--color-ink-faint)',
  };
  return (
    <Card shadow="sm" style={{ borderLeftWidth: 4, borderLeftColor: accent[tone] }}>
      <div className="text-xs uppercase tracking-wide text-ink-faint">{label}</div>
      <div className="mt-2 font-mono text-4xl text-ink">{value}</div>
    </Card>
  );
}
